import { useQuery, useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import { custom, z, ZodIssueCode } from "zod";
import { Decimal } from "decimal.js";
import {
  fetchApiGet,
  fetchApiPut,
  fetchApiPost,
  fetchApiDelete,
} from "../../utils";
import { PossibleRole } from "lhc_permissions";
import { Commodities } from "../../commodity";
import { transformToDecimal } from "../../common";
import { BaseDbObject } from "@/lib/models/db";
import { ContractTypeOptions } from "../../otc";

export enum BinaryCropTypeSelection {
  NewCrop = "new_crop",
  OldCrop = "OldCrop",
}

export const SummaryFullRowFiltersValidation = z.object({
  otc_ids: z.array(z.string().uuid()).optional(),
  closed_otc_ids: z.array(z.string().uuid()).optional(),
  general_contract_ids: z.array(z.string().uuid()).optional(),
  closed_general_contract_ids: z.array(z.string().uuid()).optional(),
  client_position_ids: z.array(z.string().uuid()).optional(),
  closed_client_position_ids: z.array(z.string().uuid()).optional(),
});

export const BinaryCropTypeSelectionOptions = [
  {
    value: BinaryCropTypeSelection.NewCrop, label: "New Crop",
  },
  {
    value: BinaryCropTypeSelection.OldCrop, label: "Old Crop",
  }
];

function preprocessOptionalBoolean(item: any): boolean | undefined {
  if (item == null) return undefined;
  if (typeof item === 'string' && !!item) return item === 'true';
  if (typeof item === 'boolean') return item;
  return undefined;
}

export function zodParseJsonPreprocessor(value: any, ctx: z.RefinementCtx) {
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch (e) {
      ctx.addIssue({
        code: ZodIssueCode.custom,
        message: (e as Error).message,
      });
    }
  }

  return value;
}

export const SummaryFiltersValidation = z.object({
  is_enabled: z.preprocess(preprocessOptionalBoolean, z.boolean().optional()),
  show_open_cash_sales: z.preprocess(preprocessOptionalBoolean, z.boolean().optional()),
  show_open_otc: z.preprocess(preprocessOptionalBoolean, z.boolean().optional()),
  show_open_client_positions: z.preprocess(preprocessOptionalBoolean, z.boolean().optional()),

  crop_year: z.coerce.number(),

  exclude_customer_ids: z.preprocess(zodParseJsonPreprocessor, z.array(z.string().uuid()).optional()),
  customer_ids: z.preprocess(zodParseJsonPreprocessor, z.array(z.string().uuid()).optional()),
  trader_ids: z.preprocess(zodParseJsonPreprocessor, z.array(z.string().uuid()).optional()),

  commodity: z.preprocess(zodParseJsonPreprocessor, z.array(z.string().uuid()).optional()),
  display_commodity_type: z.preprocess(zodParseJsonPreprocessor, z.array(z.string()).optional()),

  flag_status: z.preprocess(zodParseJsonPreprocessor, z.array(z.string()).optional()),

  include_full_revenue_details: z.preprocess(preprocessOptionalBoolean, z.boolean().optional()),
});

export const UnknownFiltersValidation = SummaryFiltersValidation.extend({
  crop_year: z.coerce.number().optional(),
})

export const SummaryDetailedFiltersValidation = SummaryFiltersValidation.extend({
  detail_view: z.string(),
});

interface CommodityCustomerSummary {
  crop_year: number,
  projected_production: Decimal,
  actual_production: Decimal,
  acreage: Decimal,

  otc_contract_ids: string[],
  closed_otc_contract_ids: string[],
  general_contract_ids: string[],
  closed_general_contract_ids: string[],
  client_position_ids: string[],
  closed_client_position_ids: string[],

  rjo_option_types: ("P" | "C")[],
  otc_contract_types_quantities: Record<string, Decimal>,
  general_contract_types_quantities: Record<string, Decimal>,

  beacon_quantity: Decimal,
  rjo_options_quantity: Decimal,
  rjo_futures_quantity: Decimal,
}

export interface ClientOpenPosition extends BaseDbObject {
  is_manually_inserted: boolean;
  is_manually_fully_paired: boolean;

  position_id: string;
  trading_account_id: string;
  customer_id?: string;
  trader_id?: string;

  enabled: boolean;
  date: Date;
  option_month: string;

  net_position: string;
  long_qty: number;
  short_qty: number;

  put_or_call?: string;
  contract: string;
  commodity?: string;
  bushel?: string;
  strike_price?: string;

  price?: Decimal | string;
  premium: Decimal | string;

  source_type?: string;
  original_row: any;

  contract_description?: string;
  symbol_code?: string;

  TradingAccount: { account_id: string };
  Customer: { id: string; long_name: string };
  Trader: { id: string; name: string };
}

// Customer ID to CommodityCustomerSummary
type CommoditySummary = Record<string, CommodityCustomerSummary>;

export interface RiskManagementSummaryResult extends BaseDbObject {
  summary: {
    // Customer ID to Customer Name
    // All Customers that appear in any summary
    commodity_customer_map: Record<string, Record<string, string>>,
    // Commodity ID (C, MW etc) to CommoditySummary
    commodity: Record<string, CommoditySummary>,
    summary: {},
  }
}

enum GroupableContractType {
  Beacon = "Beacon",
  Flat = "Flat",
  Basis = "Basis",
  HTA = "HTA",
  Spot = "Spot",
  DP = "DP",
  // MinMax = "Min Max",
  // Range = "Range",
  // PremiumOfferWDu = "Premium Offer W DU",
  // DoubleUp = "Double Up",
  // MinPriceCallSpread = "Min Price Call Spread",
  // MinPrice = "Min Price",
  // AccWeeklyWDu = "Accumulator Wkly/Du",
  // MinMaxWDu = "Min Max W DU",
  // ReownWDu = "Reown W DU",
  // HedgePlus = "HedgePlus",
}

export function generateGroupableContractTypes(item: string) {
  if (/^beacon/img.test(item)) {
    return GroupableContractType.Beacon;
  } else if (/^flat/img.test(item)) {
    return GroupableContractType.Flat;
  } else if (/^basis/img.test(item)) {
    return GroupableContractType.Basis;
  } else if (/^hta/img.test(item)) {
    return GroupableContractType.HTA;
  } else if (/^spot/img.test(item)) {
    return GroupableContractType.Spot;
  } else if (/^dp/img.test(item)) {
    return GroupableContractType.DP;
  }

  const base_type = item;
  return ContractTypeOptions.find(item => item.value ===  base_type as any)?.label || item;
}

export function useListRiskSummary({ filters }: { filters?: z.infer<typeof SummaryFiltersValidation> }) {
  const queryParams = new URLSearchParams();

  if (filters) {
    if (filters.is_enabled) {
      queryParams.set("is_enabled", filters.is_enabled.toString());
    }
    if (filters.crop_year) {
      queryParams.set("crop_year", filters.crop_year.toString());
    }
    if ((filters.trader_ids?.length ?? 0) > 0) {
      queryParams.set("trader_ids", JSON.stringify(filters.trader_ids));
    }
    if ((filters.customer_ids?.length ?? 0) > 0) {
      queryParams.set("customer_ids", JSON.stringify(filters.customer_ids));
    }
  }

  return useQuery({
    queryKey: ["risk", "summary", queryParams.toString()],
    queryFn: () =>
      fetchApiGet<RiskManagementSummaryResult>("risk/management/summary", queryParams),
    retry: 2,
    keepPreviousData: true,
  });
}

export function useListRiskSummaryFullRows({ filters }: { filters?: z.infer<typeof SummaryFullRowFiltersValidation> }) {
  const queryParams = new URLSearchParams();
  let hasAFilter = false;

  let body: any = {};

  if (filters) {
    if (Object.values(filters)) { hasAFilter = true; }
    if (filters.otc_ids?.length) {
      body.otc_ids = filters.otc_ids;
    }
    if (filters.closed_otc_ids?.length) {
      body.closed_otc_ids = filters.closed_otc_ids;
    }
    if (filters.general_contract_ids?.length) {
      body.general_contract_ids = filters.general_contract_ids;
    }
    if (filters.closed_general_contract_ids?.length) {
      body.closed_general_contract_ids = filters.closed_general_contract_ids;
    }
    if (filters.client_position_ids?.length) {
      body.client_position_ids = filters.client_position_ids;
    }
    if (filters.closed_client_position_ids?.length) {
      body.closed_client_position_ids = filters.closed_client_position_ids;
    }
  }

  return useQuery({
    queryKey: ["risk", "summaryFullRows", queryParams.toString()],
    queryFn: () =>
      fetchApiPost<RiskManagementSummaryResult>("risk/management/fullRows", body, queryParams),
    retry: 2,
    keepPreviousData: true,
    enabled: hasAFilter,
  });
}

function assembleRiskSummaryFiltersParams(filters?: z.infer<typeof SummaryFiltersValidation>) {
  const queryParams = new URLSearchParams();

  if (filters) {
    if (filters.is_enabled) {
      queryParams.set("is_enabled", filters.is_enabled.toString());
    }
    if (filters.crop_year || filters.crop_year === 0) {
      queryParams.set("crop_year", filters.crop_year.toString());
    }
    if ((filters.trader_ids?.length ?? 0) > 0) {
      queryParams.set("trader_ids", JSON.stringify(filters.trader_ids));
    }
    if ((filters.customer_ids?.length ?? 0) > 0) {
      queryParams.set("customer_ids", JSON.stringify(filters.customer_ids));
    }
    if ((filters.exclude_customer_ids?.length ?? 0) > 0) {
      queryParams.set("exclude_customer_ids", JSON.stringify(filters.exclude_customer_ids));
    }
    if (filters.display_commodity_type) {
      queryParams.set("display_commodity_type", JSON.stringify(filters.display_commodity_type));
    }
    if ((filters.flag_status?.length ?? 0) > 0) {
      queryParams.set("flag_status", JSON.stringify(filters.flag_status));
    }
    if (filters.include_full_revenue_details) {
      queryParams.set("list_revenue_details", "true");
    }
  }

  return queryParams;
}

export function useListRiskSummaryFull({ filters }: { filters?: z.infer<typeof SummaryFiltersValidation> }) {
  const queryParams = assembleRiskSummaryFiltersParams(filters);

  return useQuery({
    queryKey: ["risk", "full", queryParams.toString()],
    queryFn: () =>
      fetchApiGet<RiskManagementSummaryResult>("risk/summary", queryParams),
    retry: 2,
    keepPreviousData: false,
  });
}

export function useListRiskSummaryFullDetail({ filters }: { filters?: z.infer<typeof SummaryDetailedFiltersValidation> }) {
  const queryParams = assembleRiskSummaryFiltersParams(filters);

  if (filters && filters.detail_view) {
    queryParams.set("detail_view", filters.detail_view);
  }

  return useQuery({
    queryKey: ["risk", "detailed", queryParams.toString()],
    queryFn: () =>
      fetchApiGet<RiskManagementSummaryResult>("risk/summary/detailed", queryParams),
    retry: 2,
    keepPreviousData: false,
  });
}

export function useListRiskSummaryUnknown({ filters, enabled }: { filters?: z.infer<typeof SummaryFiltersValidation>; enabled?: boolean }) {
  const queryParams = assembleRiskSummaryFiltersParams(filters);

  return useQuery({
    queryKey: ["risk", "unknown", queryParams.toString()],
    queryFn: () =>
      fetchApiGet<RiskManagementSummaryResult>("risk/summary/unknown", queryParams),
    retry: 2,
    keepPreviousData: false,
    enabled: enabled != null ? enabled : true
  });
}

export function useFullRiskUnknown({ filters }: { filters?: z.infer<typeof UnknownFiltersValidation> }) {
  const queryParams = assembleRiskSummaryFiltersParams(filters as any);

  return useQuery({
    queryKey: ["risk", "full_unknown", queryParams.toString()],
    queryFn: () =>
      fetchApiGet<RiskManagementSummaryResult>("risk/unknown", queryParams),
    retry: 2,
    keepPreviousData: false,
  });
}

export function useFullRiskUnknownDetail({ filters }: { filters?: z.infer<typeof SummaryFiltersValidation> }) {
  const queryParams = assembleRiskSummaryFiltersParams(filters);

  return useQuery({
    queryKey: ["risk", "detail_unknown", queryParams.toString()],
    queryFn: () =>
      fetchApiGet<RiskManagementSummaryResult>("risk/unknown/detail", queryParams),
    retry: 2,
    keepPreviousData: false,
  });
}
